import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {WarehouseService} from '../../../../../services/warehouse/warehouse.service';
import {Entrepot} from '../../../../../models/entrepot';
import {getEnterpriseStationIdFromLocalStorage} from '../../../../../services/enterprise-context';

type WarehouseField = 'nom' | 'code' | 'adresse' | 'ville' | 'code_postal' | 'pays';

@Component({
    selector: 'app-edit-warehouse',
    templateUrl: './edit-warehouse.component.html',
    styleUrls: ['./edit-warehouse.component.scss']
})
export class EditWarehouseComponent implements OnInit {
    @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
    @Input() warehouseData: any = null;
    @Input() warehouse: any;
    @Output() save = new EventEmitter<any>();
    @Output() cancel = new EventEmitter<void>();
    @Output() warehouseSaved = new EventEmitter<any>();

    constructor(
        private warehouseService: WarehouseService,
        private modalService: NgbModal,
        private toastr: ToastrService
    ) {}

    currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    clientId = Number(this.currentUser?.id) || 0;
    stationId =
        getEnterpriseStationIdFromLocalStorage() ||
        Number(this.currentUser?.stations?.[0]?.station_id) ||
        Number(this.currentUser?.stations?.[0]?.id) ||
        0;

    private modalRef!: NgbModalRef;
    public loading = false;

    get isEditMode(): boolean {
        return !!(this.warehouseData && this.warehouseData.id);
    }

    entrepot: Entrepot = {
        id: 0,
        user_id: 0,
        station_id: 0,
        nom: '',
        code: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: ''
    };

    dataError: Record<WarehouseField, string> = {
        nom: '',
        code: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: ''
    };

    private readonly fieldLabels: Record<WarehouseField, string> = {
        nom: 'Nom de l\'entrepôt',
        code: 'Code entrepôt',
        adresse: 'Adresse',
        ville: 'Ville',
        code_postal: 'Code postal',
        pays: 'Pays'
    };

    ngOnInit(): void {}

    openModal() {
        if (!this.warehouseData) {
            this.resetForm();
        } else {
            this.entrepot = {
                nom: this.warehouseData.nom || '',
                code: this.warehouseData.code ?? this.warehouseData.code_entrepot ?? '',
                adresse: this.warehouseData.adresse || '',
                ville: this.warehouseData.ville || '',
                code_postal: this.warehouseData.code_postal || '',
                pays: this.warehouseData.pays || ''
            };
            this.clearAllFieldErrors();
        }
        const options: NgbModalOptions = {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            size: 'lg'
        };
        this.loading = false;
        this.modalRef = this.modalService.open(this.modalTemplate, options);
    }

    onSubmit(): void {
        if (this.loading) {
            return;
        }

        if (!this.validateRequiredFields()) {
            const missing = (Object.keys(this.dataError) as WarehouseField[])
                .filter((k) => !!this.dataError[k])
                .map((k) => this.fieldLabels[k]);
            this.toastr.warning(
                missing.length
                    ? `Champ(s) obligatoire(s) manquant(s) : ${missing.join(', ')}`
                    : 'Veuillez renseigner tous les champs obligatoires.',
                'Validation'
            );
            return;
        }

        if (!this.stationId || this.stationId <= 0) {
            this.toastr.error(
                'Impossible de déterminer la station connectée. Reconnectez-vous puis réessayez.',
                'Station manquante'
            );
            return;
        }
        if (!this.clientId || this.clientId <= 0) {
            this.toastr.error(
                'Impossible de déterminer l\'utilisateur connecté. Reconnectez-vous puis réessayez.',
                'Utilisateur manquant'
            );
            return;
        }

        const payload = {
            station_id: this.stationId,
            user_id: this.clientId,
            nom: (this.entrepot.nom || '').trim(),
            code: (this.entrepot.code || '').toString().trim(),
            adresse: (this.entrepot.adresse || '').trim(),
            ville: (this.entrepot.ville || '').trim(),
            code_postal: (this.entrepot.code_postal || '').trim(),
            pays: (this.entrepot.pays || '').trim()
        };

            this.loading = true;

        if (this.isEditMode) {
            this.warehouseService
                .updateWarehouse(this.warehouseData.id, this.clientId, this.stationId, payload)
                .subscribe({
                next: (res) => {
                    this.loading = false;
                    this.modalRef.close();
                        this.warehouseSaved.emit(res ?? {id: this.warehouseData.id});
                    this.toastr.success('Modification enregistrée avec succès !', '', {
                        positionClass: 'toast-bottom-right',
                            toastClass: 'toast ngx-toastr custom-success-toast'
                    });
                },
                error: (err) => {
                    this.loading = false;
                        this.handleApiError(err, 'modification');
                    }
                });
                return;
            }

            this.warehouseService.addWarehouse(this.stationId, payload).subscribe({
                next: (res) => {
                    this.loading = false;
                    this.modalRef.close();
                    this.warehouseSaved.emit(res);
                    this.toastr.success('Ajout enregistré avec succès !', '', {
                        positionClass: 'toast-bottom-right',
                    toastClass: 'toast ngx-toastr custom-success-toast'
                    });
                },
                error: (err) => {
                    this.loading = false;
                this.handleApiError(err, 'ajout');
            }
        });
    }

    private validateRequiredFields(): boolean {
        this.clearAllFieldErrors();
        let ok = true;

        const checks: Array<{field: WarehouseField; value: string; message: string}> = [
            {
                field: 'nom',
                value: this.entrepot.nom,
                message: 'Le nom de l\'entrepôt est obligatoire.'
            },
            {
                field: 'code',
                value: this.entrepot.code,
                message: 'Le code entrepôt est obligatoire.'
            },
            {
                field: 'adresse',
                value: this.entrepot.adresse,
                message: 'L\'adresse est obligatoire.'
            },
            {
                field: 'ville',
                value: this.entrepot.ville,
                message: 'La ville est obligatoire.'
            },
            {
                field: 'code_postal',
                value: this.entrepot.code_postal,
                message: 'Le code postal est obligatoire.'
            },
            {
                field: 'pays',
                value: this.entrepot.pays,
                message: 'Le pays est obligatoire. Veuillez sélectionner un pays.'
            }
        ];

        for (const c of checks) {
            if (!c.value || !String(c.value).trim()) {
                this.dataError[c.field] = c.message;
                ok = false;
            }
        }
        return ok;
    }

    private handleApiError(err: unknown, action: 'ajout' | 'modification'): void {
        const details = this.extractApiErrorDetails(err);
        this.applyApiErrorsToFields(details.fieldMessages);

        const title = action === 'ajout' ? 'Erreur lors de l\'ajout' : 'Erreur lors de la modification';
        const msg =
            details.summary && !this.isGenericHttpStatus(details.summary)
                ? details.summary
                : 'La requête est invalide. Vérifiez les champs obligatoires (notamment le pays).';

        this.toastr.error(msg, title);
    }

    private extractApiErrorDetails(err: unknown): {
        summary: string;
        fieldMessages: Partial<Record<WarehouseField, string>>;
    } {
        const fieldMessages: Partial<Record<WarehouseField, string>> = {};
        let summary = '';

        if (typeof err === 'string') {
            summary = err.trim();
            this.mapErrorTextToFields(summary, fieldMessages);
            return {summary, fieldMessages};
        }

        const httpErr = err as {
            error?: any;
            message?: string;
            statusText?: string;
            status?: number;
        };
        const body = httpErr?.error;

        if (body && typeof body === 'object') {
            if (Array.isArray(body.errors) && body.errors.length) {
                const list = body.errors.map((e: unknown) => String(e));
                summary = list.join(' ; ');
                for (const line of list) {
                    this.mapErrorTextToFields(line, fieldMessages);
                }
            } else if (typeof body.message === 'string' && body.message.trim()) {
                summary = body.message.trim();
                this.mapErrorTextToFields(summary, fieldMessages);
            }
        } else if (typeof body === 'string' && body.trim()) {
            summary = body.trim();
        }

        if (!summary && typeof httpErr?.message === 'string') {
            summary = httpErr.message.trim();
        }
        if (!summary && typeof httpErr?.statusText === 'string') {
            summary = httpErr.statusText.trim();
        }

        return {summary, fieldMessages};
    }

    private mapErrorTextToFields(
        text: string,
        target: Partial<Record<WarehouseField, string>>
    ): void {
        const t = (text || '').toLowerCase();
        const mapping: Array<{keys: string[]; field: WarehouseField; label: string}> = [
            {keys: ['pays'], field: 'pays', label: 'Le pays est obligatoire ou invalide.'},
            {keys: ['code_postal', 'code postal'], field: 'code_postal', label: 'Le code postal est obligatoire ou invalide.'},
            {keys: ['ville'], field: 'ville', label: 'La ville est obligatoire ou invalide.'},
            {keys: ['adresse'], field: 'adresse', label: 'L\'adresse est obligatoire ou invalide.'},
            {keys: ['nom'], field: 'nom', label: 'Le nom de l\'entrepôt est obligatoire ou invalide.'},
            {keys: ['code'], field: 'code', label: 'Le code entrepôt est obligatoire ou invalide.'}
        ];
        for (const m of mapping) {
            if (m.keys.some((k) => t.includes(k))) {
                if (!target[m.field]) {
                    target[m.field] = m.label;
                }
            }
        }
    }

    private applyApiErrorsToFields(fieldMessages: Partial<Record<WarehouseField, string>>): void {
        for (const key of Object.keys(fieldMessages) as WarehouseField[]) {
            const msg = fieldMessages[key];
            if (msg) {
                this.dataError[key] = msg;
            }
        }
    }

    private isGenericHttpStatus(msg: string): boolean {
        const m = (msg || '').trim().toLowerCase();
        return (
            !m ||
            m === 'bad request' ||
            m === 'unknown error' ||
            m === 'ok' ||
            m === 'http error' ||
            /^http\s*400/.test(m)
        );
    }

    private resetForm() {
        this.entrepot = {
            nom: '',
            code: '',
            adresse: '',
            ville: '',
            code_postal: '',
            pays: ''
        };
        this.warehouseData = null;
        this.clearAllFieldErrors();
    }

    private clearAllFieldErrors(): void {
        this.dataError = {
            nom: '',
            code: '',
            adresse: '',
            ville: '',
            code_postal: '',
            pays: ''
        };
    }

    clearFieldError(fieldName: string): void {
        if (fieldName && this.dataError[fieldName as WarehouseField] !== undefined) {
            this.dataError[fieldName as WarehouseField] = '';
        }
    }
}
